import axios from 'axios';
import { reportPossibleOutage, reportHealthy } from './maintenance';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || process.env.REACT_APP_API_URL;

if (!BACKEND_URL) {
  // eslint-disable-next-line no-console
  console.error('[api] Neither REACT_APP_BACKEND_URL nor REACT_APP_API_URL is set. API calls will fail.');
}
export const API_BASE = `${BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API_BASE,
});

// ── Performance debug logging ──────────────────────────────────────────────
// Logs every API call with timing + size, ONLY when enabled. To turn on:
//   localStorage.setItem('perf_debug', '1')   then reload
// To turn off:
//   localStorage.removeItem('perf_debug')
// Zero impact for normal users — the flag is off by default.
const perfOn = () => {
  try { return localStorage.getItem('perf_debug') === '1'; } catch { return false; }
};

// Add auth token to all requests.
// Token persists in localStorage so users stay logged in across browser sessions.
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  if (perfOn()) config.metadata = { start: performance.now() };
  return config;
});

// ── Timezone fix ────────────────────────────────────────────────────────────
// The backend stores times in UTC (datetime.utcnow()) but serialises them
// WITHOUT a timezone marker (e.g. "2026-06-26T07:49:10"). The browser would
// otherwise read those as LOCAL time, making fresh comments/logs look hours off.
// We append "Z" to naive ISO datetimes so new Date()/date-fns treat them as UTC.
const NAIVE_DATETIME = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?$/;
const normalizeDates = (val) => {
  if (typeof val === 'string') {
    return NAIVE_DATETIME.test(val) ? `${val}Z` : val;
  }
  if (Array.isArray(val)) {
    for (let i = 0; i < val.length; i += 1) val[i] = normalizeDates(val[i]);
    return val;
  }
  if (val && typeof val === 'object') {
    for (const k in val) {
      if (Object.prototype.hasOwnProperty.call(val, k)) val[k] = normalizeDates(val[k]);
    }
    return val;
  }
  return val;
};

// Handle unauthorized responses + perf timing
api.interceptors.response.use(
  (response) => {
    // Mark naive UTC timestamps so the UI shows correct local times everywhere.
    try {
      const ct = response.headers?.['content-type'] || '';
      if (ct.includes('application/json') && response.data && typeof response.data === 'object') {
        response.data = normalizeDates(response.data);
      }
    } catch { /* never block a response on this */ }
    reportHealthy();  // a successful response clears any stale maintenance state
    if (perfOn() && response.config.metadata) {
      const ms = Math.round(performance.now() - response.config.metadata.start);
      let size = response.headers['content-length'];
      if (!size) { try { size = JSON.stringify(response.data).length; } catch { size = '?'; } }
      const color = ms > 800 ? '#e2445c' : ms > 400 ? '#fdab3d' : '#00c875';
      const method = (response.config.method || 'get').toUpperCase();
      // Strip the baseURL for readability
      const url = (response.config.url || '').replace(API_BASE, '');
      console.log(
        `%c[API ${ms}ms] %c${method} ${url} %c(${size}b)`,
        `color:${color};font-weight:bold`, 'color:#888', 'color:#bbb'
      );
    }
    return response;
  },
  (error) => {
    if (perfOn() && error.config?.metadata) {
      const ms = Math.round(performance.now() - error.config.metadata.start);
      console.log(`%c[API ${ms}ms FAILED] ${(error.config.method || 'get').toUpperCase()} ${(error.config.url || '').replace(API_BASE, '')} → ${error.response?.status || 'network error'}`, 'color:#e2445c;font-weight:bold');
    }
    // Backend unreachable (network error) or server error (5xx) → confirm via
    // /health and, if truly down, flip the app into maintenance mode.
    if (!error.response || error.response.status >= 500) {
      reportPossibleOutage();
    }
    if (error.response?.status === 401) {
      // Don't redirect on login failures — the login page handles those itself.
      // Only redirect when an authenticated request gets a 401 (expired/invalid token).
      const isLoginRequest = error.config?.url?.includes('/auth/login') || error.config?.url?.includes('/auth/microsoft');
      if (!isLoginRequest) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        localStorage.removeItem('session_id');
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

export default api;
